import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  SportsEsports as GameIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useChatContext } from '../../contexts/ChatContext';
import { useConsole } from '../../contexts/ConsoleContext';
import { FIRST_USE_KEY } from '../chat/ChatInput';
import { listPlayers, deletePlayer, startNewGame } from '../../services/gameInitialization';
import { PlayerStateManager } from '../../utils/playerState';
import { uiLogger } from '../../services/uiLogger';

interface Player {
  id: string;
  username: string;
  display_name: string;
  credits: number;
  current_system_id: string;
  created_at: string;
}

interface GamePanelProps {
  playerId: string;
}

export function GamePanel({ playerId: currentPlayerId }: GamePanelProps) {
  const [players, setPlayers] = useState<Player[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState<string | null>(null);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const { clearMessages } = useChatContext();
  const { setSelectedPanel } = useConsole();

  // Load players on mount
  useEffect(() => {
    loadPlayers();
  }, []);

  const loadPlayers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const playerList = await listPlayers();
      setPlayers(playerList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load players');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNewGame = () => {
    uiLogger.button('GamePanel', 'create_new_game');
    setPlayerName(''); // Reset name
    setNameDialogOpen(true); // Open dialog instead of creating immediately
  };

  const handleConfirmCreate = async () => {
    setNameDialogOpen(false);
    setIsCreating(true);
    setError(null);
    setSuccess(null);

    const result = await startNewGame(undefined, playerName.trim());

    if (result.success && result.playerId) {
      // Update localStorage to new player ID
      PlayerStateManager.setPlayerId(result.playerId);

      // Clear chat messages
      clearMessages();

      // Reload players list to show the new game
      await loadPlayers();

      // Show success message and reset loading state
      setSuccess('New game created! Redirecting to game...');
      setIsCreating(false);

      // Navigate to chat panel after a delay to let user see the new game in list
      setTimeout(() => {
        setSelectedPanel('chat');
        localStorage.removeItem(FIRST_USE_KEY);
        window.location.reload();
      }, 1500);
    } else {
      setError(result.error || 'Unknown error occurred');
      setIsCreating(false);
    }
  };

  const handleSelectPlayer = (playerId: string) => {
    if (playerId === currentPlayerId) return;

    uiLogger.button('GamePanel', 'switch_player', { from: currentPlayerId, to: playerId });

    // Update localStorage
    PlayerStateManager.setPlayerId(playerId);

    // Clear chat and reload
    clearMessages();
    localStorage.removeItem(FIRST_USE_KEY);
    window.location.reload();
  };

  const handleDeletePlayer = async () => {
    if (!playerToDelete) return;

    uiLogger.button('GamePanel', 'delete_player', { playerId: playerToDelete });

    setConfirmDialogOpen(false);
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await deletePlayer(playerToDelete);
      setSuccess(`Player deleted successfully`);

      // If we deleted the current player, we need to switch or create a new one
      if (playerToDelete === currentPlayerId) {
        // Reload players and pick first one or create new
        const updatedPlayers = await listPlayers();
        if (updatedPlayers.length > 0) {
          // Switch to first available player
          PlayerStateManager.setPlayerId(updatedPlayers[0].id);
          setPlayers(updatedPlayers);
          window.location.reload();
        } else {
          // No players left, create a new one
          const result = await startNewGame();
          if (result.success && result.playerId) {
            PlayerStateManager.setPlayerId(result.playerId);
            window.location.reload();
          }
        }
      } else {
        // Just reload the list
        await loadPlayers();
        setIsLoading(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete player');
      setIsLoading(false);
    }

    setPlayerToDelete(null);
  };

  const openDeleteDialog = (playerId: string) => {
    setPlayerToDelete(playerId);
    setConfirmDialogOpen(true);
  };

  return (
    <Card
      className="panel"
      sx={{
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <CardContent className="panel-header" sx={{ p: 1.5, pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 700,
              letterSpacing: 1,
              fontSize: '0.65rem',
            }}
          >
            Game Management
          </Typography>
          <GameIcon sx={{ fontSize: 16, color: 'primary.main' }} />
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
          Manage your saved games and start new adventures
        </Typography>
      </CardContent>

      <Box className="panel-scroll" sx={{ px: 1.5, pb: 1.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Create New Game Button */}
        <Button
          variant="contained"
          color="primary"
          startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          onClick={handleCreateNewGame}
          disabled={isCreating || isLoading}
          fullWidth
          sx={{ mb: 3 }}
        >
          {isCreating ? 'Creating Game...' : 'Create New Game'}
        </Button>

        {/* Players List */}
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
          Saved Games
        </Typography>

        {isLoading && !isCreating ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : players.length === 0 ? (
          <Alert severity="info">
            No saved games found. Create a new game to get started.
          </Alert>
        ) : (
          <List sx={{ bgcolor: 'background.default', borderRadius: 1, p: 0 }}>
            {players.map((player) => {
              const isActive = player.id === currentPlayerId;
              return (
                <ListItem
                  key={player.id}
                  disablePadding
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 },
                  }}
                >
                  <ListItemButton
                    onClick={() => handleSelectPlayer(player.id)}
                    sx={{
                      bgcolor: isActive ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: isActive ? 'action.selected' : 'action.hover',
                      },
                      cursor: isActive ? 'default' : 'pointer',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: isActive ? 600 : 400 }}>
                            {player.display_name || player.username}
                          </Typography>
                          {isActive && (
                            <Chip
                              label="Active"
                              size="small"
                              color="primary"
                              icon={<CheckCircleIcon />}
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          {player.credits.toLocaleString()} credits • {player.current_system_id}
                          <br />
                          Created: {new Date(player.created_at).toLocaleDateString()}
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteDialog(player.id);
                        }}
                        disabled={isLoading || isCreating}
                        sx={{
                          color: 'error.main',
                          '&:hover': { bgcolor: 'error.dark', color: 'error.contrastText' },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Confirm Delete Game
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this saved game?
            <br /><br />
            <strong>This will permanently delete:</strong>
            <ul>
              <li>All player progress (credits, location, missions)</li>
              <li>Ship and cargo</li>
              <li>All chat history</li>
              <li>Faction standings and relationships</li>
            </ul>
            <br />
            <strong>This action cannot be undone!</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeletePlayer}
            color="error"
            variant="contained"
            autoFocus
          >
            Yes, Delete Game
          </Button>
        </DialogActions>
      </Dialog>

      {/* Player Name Dialog */}
      <Dialog open={nameDialogOpen} onClose={() => setNameDialogOpen(false)}>
        <DialogTitle>Create New Game</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter a name for your captain:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Captain Name"
            fullWidth
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && playerName.trim()) {
                handleConfirmCreate();
              }
            }}
            placeholder="Captain"
            helperText="Leave blank to use default 'Captain'"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmCreate} variant="contained" color="primary">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
